import pino from "pino";
//#region src/utils/uid.ts
function generateUID() {
	return Math.random().toString(36).substring(2);
}
//#endregion
//#region src/enums/workflow.ts
const Step = {
	__STEP__: "__STEP__",
	INTRO: "INTRO",
	MOBILE_REDIRECT: "MOBILE_REDIRECT",
	MOBILE_REDIRECT_CANCELED: "MOBILE_REDIRECT_CANCELED",
	DOCUMENT_FRONT: "DOCUMENT_FRONT",
	DOCUMENT_BACK: "DOCUMENT_BACK",
	DOCUMENT_OCR_FRONT: "DOCUMENT_OCR_FRONT",
	DOCUMENT_OCR_BACK: "DOCUMENT_OCR_BACK",
	DOCUMENT_OCR_EVALUATE: "DOCUMENT_OCR_EVALUATE",
	AGE_FACE: "AGE_FACE",
	AGE_DOCUMENT_FRONT: "AGE_DOCUMENT_FRONT",
	AGE_FACE_EVALUATE: "AGE_FACE_EVALUATE",
	AGE_DOCUMENT_EVALUATE: "AGE_DOCUMENT_EVALUATE",
	FACE: "FACE",
	SUCCESS: "SUCCESS",
	FAIL: "FAIL"
};
const FailReason = {
	NOT_FOUND: "NOT_FOUND",
	SESSION_EXPIRED: "SESSION_EXPIRED",
	UNAUTHORIZED: "UNAUTHORIZED",
	SUSPENDED: "SUSPENDED",
	BAD_LIGHTNING_OR_LOW_QUALITY: "BAD_LIGHTNING_OR_LOW_QUALITY",
	DOCUMENT_CAPTURED_INCORRECTLY: "DOCUMENT_CAPTURED_INCORRECTLY",
	DOCUMENT_NOT_ALLOWED: "DOCUMENT_NOT_ALLOWED",
	DOCUMENT_NOT_RECOGNIZED: "DOCUMENT_NOT_RECOGNIZED",
	UNABLE_TO_VERIFY: "UNABLE_TO_VERIFY"
};
const ActionType = {
	REDIRECT: "REDIRECT",
	RETRY: "RETRY",
	CONTINUE: "CONTINUE",
	PROCESS: "PROCESS",
	EVALUATE: "EVALUATE",
	OPTIONS: "OPTIONS",
	SKIP: "SKIP"
};
//#endregion
//#region src/enums/enums.ts
const Locale = {
	CZ: "cz",
	EN: "en",
	ES: "es",
	HU: "hu",
	PT: "pt",
	SK: "sk"
};
const VerificationState = {
	ABANDONED: "ABANDONED",
	REJECTED: "REJECTED",
	VERIFIED: "VERIFIED"
};
const SystemErrorStatus = {
	NOT_FOUND: "NOT_FOUND",
	PALM_FEATURE_DISABLED: "PALM_FEATURE_DISABLED",
	SESSION_EXPIRED: "SESSION_EXPIRED",
	UNAUTHORIZED: "UNAUTHORIZED"
};
const BusinessErrorStatus = {
	BAD_LIGHTNING_OR_LOW_QUALITY: "BAD_LIGHTNING_OR_LOW_QUALITY",
	DOCUMENT_CAPTURED_INCORRECTLY: "DOCUMENT_CAPTURED_INCORRECTLY",
	DOCUMENT_NOT_ALLOWED: "DOCUMENT_NOT_ALLOWED",
	DOCUMENT_NOT_RECOGNIZED: "DOCUMENT_NOT_RECOGNIZED"
};
const ErrorStatus = {
	NOT_FOUND: "NOT_FOUND",
	SESSION_EXPIRED: "SESSION_EXPIRED",
	UNAUTHORIZED: "UNAUTHORIZED",
	BAD_LIGHTNING_OR_LOW_QUALITY: "BAD_LIGHTNING_OR_LOW_QUALITY",
	DOCUMENT_CAPTURED_INCORRECTLY: "DOCUMENT_CAPTURED_INCORRECTLY",
	DOCUMENT_NOT_ALLOWED: "DOCUMENT_NOT_ALLOWED",
	DOCUMENT_NOT_RECOGNIZED: "DOCUMENT_NOT_RECOGNIZED",
	UNABLE_TO_VERIFY: "UNABLE_TO_VERIFY"
};
const VerificationResult = {
	NOT_FOUND: "NOT_FOUND",
	SESSION_EXPIRED: "SESSION_EXPIRED",
	UNAUTHORIZED: "UNAUTHORIZED",
	BAD_LIGHTNING_OR_LOW_QUALITY: "BAD_LIGHTNING_OR_LOW_QUALITY",
	DOCUMENT_CAPTURED_INCORRECTLY: "DOCUMENT_CAPTURED_INCORRECTLY",
	DOCUMENT_NOT_ALLOWED: "DOCUMENT_NOT_ALLOWED",
	DOCUMENT_NOT_RECOGNIZED: "DOCUMENT_NOT_RECOGNIZED",
	UNABLE_TO_VERIFY: "UNABLE_TO_VERIFY",
	SUCCESS: "SUCCESS"
};
/**
* The following codes are logged on BE and mapped to VERIFICATION_RESULT
* @see https://docs.google.com/spreadsheets/d/1ybyOE_YrA1PpOlzQof3xmpbwUeTQ-Gf3hOP12pIGAJg/edit?pli=1#gid=0
*/
let ErrorCode = /* @__PURE__ */ function(ErrorCode) {
	ErrorCode["ALREADY_EXISTS"] = "ALREADY_EXISTS";
	ErrorCode["CANNOT_INIT_DETECTOR"] = "CANNOT_INIT_DETECTOR";
	ErrorCode["CONFLICT"] = "CONFLICT";
	ErrorCode["CORRUPTED_TEMPLATE"] = "CORRUPTED_TEMPLATE";
	ErrorCode["CUSTOMER_CALLBACK"] = "CUSTOMER_CALLBACK";
	ErrorCode["DOCUMENT_EXPIRED"] = "DOCUMENT_EXPIRED";
	ErrorCode["DOCUMENT_NOT_AUTHENTIC"] = "DOCUMENT_NOT_AUTHENTIC";
	ErrorCode["DOCUMENT_NOT_RECOGNIZED"] = "DOCUMENT_NOT_RECOGNIZED";
	ErrorCode["DOCUMENT_TYPE_UNKNOWN"] = "DOCUMENT_TYPE_UNKNOWN";
	ErrorCode["FACE_SIZE_MEMORY_LIMIT"] = "FACE_SIZE_MEMORY_LIMIT";
	ErrorCode["INCOMPATIBLE_TEMPLATE"] = "INCOMPATIBLE_TEMPLATE";
	ErrorCode["INVALID_DATA"] = "INVALID_DATA";
	ErrorCode["INVALID_ID"] = "INVALID_ID";
	ErrorCode["INVALID_IMAGE"] = "INVALID_IMAGE";
	ErrorCode["INVALID_REQUEST_BODY"] = "INVALID_REQUEST_BODY";
	ErrorCode["INVALID_REQUEST_METHOD"] = "INVALID_REQUEST_METHOD";
	ErrorCode["INVALID_REQUEST_PARAMETER"] = "INVALID_REQUEST_PARAMETER";
	ErrorCode["LIVENESS_CHECK_FAILED"] = "LIVENESS_CHECK_FAILED";
	ErrorCode["MAX_RETRIES_EXCEEDED"] = "MAX_RETRIES_EXCEEDED";
	ErrorCode["NO_CARD_CORNERS_DETECTED"] = "NO_CARD_CORNERS_DETECTED";
	ErrorCode["NFC_REQUIRED_NOT_CAPTURED"] = "NFC_REQUIRED_NOT_CAPTURED";
	/** SAIDVU-499: palm capture rejected (retryable) — `details.reason` carries a PalmCaptureRejectionReason. */
	ErrorCode["PALM_CAPTURE_REJECTED"] = "PALM_CAPTURE_REJECTED";
	/** SAIDVU-499: D40 palm retake budget exhausted → workflow terminated Incomplete (`palm_not_captured`). */
	ErrorCode["PALM_REQUIRED_NOT_CAPTURED"] = "PALM_REQUIRED_NOT_CAPTURED";
	ErrorCode["NO_FACE_DETECTED"] = "NO_FACE_DETECTED";
	ErrorCode["NOT_ENOUGH_DATA"] = "NOT_ENOUGH_DATA";
	ErrorCode["NOT_FOUND"] = "NOT_FOUND";
	ErrorCode["PAGE_DOESNT_MATCH_DOCUMENT_TYPE_OF_PREVIOUS_PAGE"] = "PAGE_DOESNT_MATCH_DOCUMENT_TYPE_OF_PREVIOUS_PAGE";
	ErrorCode["PERSON_DOCUMENT_MISMATCH"] = "PERSON_DOCUMENT_MISMATCH";
	ErrorCode["REALLY_UNEXPECTED_ERROR"] = "REALLY_UNEXPECTED_ERROR";
	ErrorCode["UNEXPECTED_ERROR"] = "UNEXPECTED_ERROR";
	ErrorCode["UNKNOWN_FACE_MATCH"] = "UNKNOWN_FACE_MATCH";
	ErrorCode["UNSUPPORTED_VERSION_TEMPLATE"] = "UNSUPPORTED_VERSION_TEMPLATE";
	return ErrorCode;
}({});
const LOCALES = Object.values(Locale);
const VERIFICATION_STATES = Object.values(VerificationState);
const ERROR_CODES = Object.values(ErrorCode);
const URL_VALIDATION_REGEX = /^(?!.*\/$)(https?:\/\/[^\s/$.?#].[^\s]*)$/;
const INNOVATRICS_CONTACT_PAGE = "https://www.innovatrics.com/contact/";
const DIS_ERROR_CODE = {
	INVALID_DATA: "INVALID_DATA",
	NO_FACE_DETECTED: "NO_FACE_DETECTED",
	NOT_ENOUGH_DATA: "NOT_ENOUGH_DATA",
	PAGE_DOESNT_MATCH_DOCUMENT_TYPE_OF_PREVIOUS_PAGE: "PAGE_DOESNT_MATCH_DOCUMENT_TYPE_OF_PREVIOUS_PAGE",
	NO_CARD_CORNERS_DETECTED: "NO_CARD_CORNERS_DETECTED",
	DOCUMENT_TYPE_NOT_RECOGNIZED: "DOCUMENT_TYPE_NOT_RECOGNIZED",
	MISSING_FRONT_PAGE: "MISSING_FRONT_PAGE",
	/** eMRTD chip response failed LDS parsing (NFC_capture chip-data upload). SAIDVU-360. */
	LDS_PARSE_ERROR: "LDS_PARSE_ERROR",
	/** Combined classification changed after an additional page upload — informational; the response body still carries the reclassified document. SAIDVU-382. */
	PAGE_CLASSIFICATION_CHANGED: "PAGE_CLASSIFICATION_CHANGED"
};
const ERROR_DESCRIPTIONS = {
	INVALID_DATA: "Invalid or corrupted data provided",
	NO_FACE_DETECTED: "No face detected in the image",
	NOT_ENOUGH_DATA: "Insufficient data for processing",
	PAGE_DOESNT_MATCH_DOCUMENT_TYPE_OF_PREVIOUS_PAGE: "Page type mismatch with previous page",
	NO_CARD_CORNERS_DETECTED: "Document corners not detected properly",
	DOCUMENT_TYPE_NOT_RECOGNIZED: "Document type could not be identified",
	MISSING_FRONT_PAGE: "Only the back page of the document was uploaded",
	LDS_PARSE_ERROR: "Chip data could not be parsed (LDS structure invalid or corrupted)",
	PAGE_CLASSIFICATION_CHANGED: "Document classification changed after the additional page was uploaded"
};
const ERROR_RESPONSE_ACTION = {
	RETRY: "RETRY",
	FAIL: "FAIL",
	CONTINUE: "CONTINUE"
};
const CustomFlowType = {
	Idv: "idv",
	AgeVerification: "age_verification",
	Ocr: "ocr"
};
/**
* Terminal workflow outcomes shown in the dashboard. SAIDVU-243 Phase 9 aligned the values
* with the D-tree decision vocabulary (accepted/rejected/review/failed).
*
* `accepted` was previously named `verified` — the rename happened in the BFF
* `transaction_report.onboarding_result` enum at the same time (`20260528120000` migration
* backfills historical rows).
*/
const OnboardingResult = {
	ACCEPTED: "accepted",
	REJECTED: "rejected",
	REVIEW: "review",
	FAILED: "failed"
};
const ConnectionType = {
	Api: "api",
	NoCode: "nocode"
};
const Environment = {
	SANDBOX: "SANDBOX",
	PRODUCTION: "PRODUCTION"
};
const FilterAll = "all";
const PlatformHealthStatus = {
	OPERATIONAL: "operational",
	DEGRADED: "degraded",
	DOWN: "down"
};
//#endregion
//#region src/types/audit.ts
/**
* SAIDVU-243 — shared audit log contracts.
*
* Audit events live in `config-service.audit_events`. The DB schema is
* intentionally lean — only `action` + entity reference (`entity_type` /
* `entity_id`) are indexable columns; everything else (field-level edit data,
* reason codes, comments, actor names) is stored in the `diff` JSON column.
*
* Per-action diff shapes are declared here so emitters (onboarding-service,
* dashboard-service) and consumers (dashboard FE Audit Log tab, D49 webhook
* emitter) share one source of truth without coupling to either Prisma client.
*/
/** Reviewer / operator actions (operator portal mutations). */
const ReviewAuditAction = {
	adminCustomersExported: "review.admin.customers_exported",
	adminIdvSettingsUpdated: "review.admin.idv_settings.updated",
	concurrentResolved: "review.identity.concurrent_resolved",
	customerAnonymized: "review.customer.anonymized",
	customerFieldEdited: "review.customer.field_edited",
	customerPutOnBlocklist: "review.customer.put_on_blocklist",
	identityAnonymized: "review.identity.anonymized",
	identityAssigneesUpdated: "review.identity.assignees_updated",
	identityCustomerUpdated: "review.identity.customer_updated",
	identityDecisionSet: "review.identity.decision_set",
	identityFieldEdited: "review.identity.field_edited",
	identityImageAccessed: "review.identity.image_accessed",
	identityImageAccessDenied: "review.identity.image_access_denied",
	identityNoteCreated: "review.identity.note_created",
	identityResetToReview: "review.identity.reset_to_review"
};
/** Integrator API mutations (M2M). Actor === tenant (Keycloak client_id). */
const IntegratorAuditAction = {
	assignmentCreated: "integrator.assignment.created",
	assignmentDeleted: "integrator.assignment.deleted",
	assignmentResumed: "integrator.assignment.resumed",
	assignmentSuspended: "integrator.assignment.suspended",
	assignmentTransferred: "integrator.assignment.transferred",
	assignmentUpdated: "integrator.assignment.updated",
	identityAnonymized: "integrator.identity.anonymized",
	identityTransitioned: "integrator.identity.transitioned"
};
/**
* System actions emitted by the workflow runtime / sync helpers. Mirrors the
* D49 `record.action` enum — SAIDVU-197 S4 reads from the same source.
*/
const SystemAuditAction = {
	concurrentResolved: "system.identity.concurrent_resolved",
	customerAnonymized: "system.customer.anonymized",
	customerBlocked: "system.customer.blocked",
	customerDecisionSynced: "system.customer.decision_synced",
	hitlistPropagated: "system.identity.hitlist_propagated",
	identityCreated: "system.identity.created",
	identityDecisionEvaluated: "system.identity.decision_evaluated",
	identityMerged: "system.identity.merged",
	identityWorkflowCompleted: "system.identity.workflow_completed",
	identityWorkflowStarted: "system.identity.workflow_started",
	trustFactorEvaluated: "system.trust_factor.evaluated"
};
/** Tenant-lifecycle actions (existing, written from dashboard-service). */
const TenantAuditAction = {
	apiKeyCreated: "api_key.created",
	apiKeyRotated: "api_key.rotated",
	/** SAIDVU-15: per-tenant document-dedup config edits (config-service). */
	dedupDocumentConfigUpdated: "config.dedup_document.updated",
	/** SAIDVU-520: per-tenant face-dedup config edits (config-service). */
	dedupFaceConfigUpdated: "config.dedup_face.updated",
	environmentSwitchedToProduction: "environment.switched_to_production",
	tenantConfigUpdated: "tenant.config.updated"
};
function deriveActorType(action) {
	if (action.startsWith("system.")) return "system";
	if (action.startsWith("integrator.")) return "integrator";
	return "operator";
}
/** Recognized entity types in `audit_events.entity_type`. */
const AuditEntityType = {
	Assignment: "Assignment",
	Customer: "Customer",
	DigitalIdentity: "DigitalIdentity",
	Tenant: "Tenant",
	TrustFactorEvaluation: "TrustFactorEvaluation"
};
/** SAIDVU-324 — Supervisor explanation persisted on reset-to-review audit rows. */
const RESET_TO_REVIEW_REASON_MIN_LENGTH = 10;
const RESET_TO_REVIEW_REASON_MAX_LENGTH = 500;
function normalizeResetToReviewReasonText(value) {
	return value.trim();
}
function isValidResetToReviewReasonText(value) {
	const trimmed = normalizeResetToReviewReasonText(value);
	return trimmed.length >= 10 && trimmed.length <= 500;
}
/**
* PII field keys scrubbed from audit `diff.before` / `diff.after` on anonymize
* (SAIDVU-250 Q4, invoked by the SAIDVU-246 anonymize handler). The PII *value*
* is replaced with the sentinel `"[anonymized]"`; the key is kept so the audit
* log still shows that a field was edited.
*
* Keyed by the value actually stored in `audit_events.entity_type` (lowercase
* `customer` / `digital_identity` / `document` / `assignment`) — NOT the
* PascalCase `AuditEntityType` values. Field names are the as-built camelCase
* keys present in stored diffs (Prisma model fields), with snake_case aliases
* for forward-compat with the SAIDVU-250 target schema / D49 `record.action`.
*
* `digital_identity` is the union of customer + document PII because customer
* field-edits are recorded on the DI-scoped audit row (nested under `customer`),
* and DI detail snapshots may embed document fields. `assignment` carries no PII
* (D47) and is intentionally empty — Assignment rows survive anonymize unchanged.
*/
const CUSTOMER_PII_FIELDS = [
	"name",
	"givenName",
	"surname",
	"placeOfBirth",
	"dateOfBirth",
	"dateOfBirthRaw",
	"personalNumber",
	"given_name",
	"place_of_birth",
	"date_of_birth",
	"date_of_birth_raw",
	"personal_number"
];
const DOCUMENT_PII_FIELDS = [
	"documentNumber",
	"fullName",
	"givenName",
	"surname",
	"citizenship",
	"mrz",
	"personalNumber",
	"dateOfBirth",
	"dateOfIssue",
	"dateOfExpiry",
	"sparseData",
	"document_number",
	"full_name",
	"given_name",
	"personal_number",
	"date_of_birth",
	"date_of_issue",
	"date_of_expiry",
	"sparse_data"
];
const PII_FIELDS_BY_ENTITY = {
	assignment: [],
	customer: CUSTOMER_PII_FIELDS,
	digital_identity: [.../* @__PURE__ */ new Set([...CUSTOMER_PII_FIELDS, ...DOCUMENT_PII_FIELDS])],
	document: DOCUMENT_PII_FIELDS
};
/** Sentinel written in place of PII values in sanitized audit diffs. */
const ANONYMIZED_SENTINEL = "[anonymized]";
//#endregion
//#region src/types/document-ocr-quality.ts
/**
* Closed retake-reason enum (SAIDVU-301 Q1). `Unknown` is never emitted by
* `evaluateDocumentOcrQuality` — every failure path returns a specific code.
* It's kept as part of the frozen enum contract so integrators writing an
* exhaustive switch have a safe default if a future check is added without a
* matching enum member yet.
*/
const DocumentOcrRetakeReasonCode = {
	DobAgeOutOfRange: "DOB_AGE_OUT_OF_RANGE",
	DoeDurationOutOfRange: "DOE_DURATION_OUT_OF_RANGE",
	DoiAgeOutOfRange: "DOI_AGE_OUT_OF_RANGE",
	FieldLengthMismatch: "FIELD_LENGTH_MISMATCH",
	InvalidDob: "INVALID_DOB",
	InvalidDoe: "INVALID_DOE",
	InvalidDoi: "INVALID_DOI",
	LowOcrConfidence: "LOW_OCR_CONFIDENCE",
	MrzChecksum: "MRZ_CHECKSUM",
	MrzInconsistent: "MRZ_INCONSISTENT",
	PortraitCovered: "PORTRAIT_COVERED",
	Unknown: "UNKNOWN"
};
const DOCUMENT_OCR_RETAKE_REASON_CODES = Object.values(DocumentOcrRetakeReasonCode);
/** BFF-driven audit action (SAIDVU-301 Q5). */
const DigitalIdentityAuditAction = { documentRecaptured: "digital_identity.document_recaptured" };
const ConfigAuditAction = { documentOcrQualityUpdated: "config.document_ocr_quality.updated" };
const DEFAULT_DOCUMENT_OCR_QUALITY_CONFIG = {
	name: "Document OCR quality check configuration",
	version: 1,
	minOcrConfidence: .5,
	portraitPhotoCoveredValidation: false,
	dobMinAgeYears: 0,
	dobMaxAgeYears: 120,
	doiMinAgeYears: 0,
	doiMaxAgeYears: 100,
	doiAcceptMissingIssueDate: false,
	doeMinDurationYears: 0,
	doeMaxDurationYears: 100,
	doeAcceptNoExpiration: false,
	mrzTextConsistencyValidation: true,
	mrzChecksumValidation: true,
	fieldFixedLengthValidation: [{
		country: "SVK",
		type: "identity-card",
		field: "document.documentNumber",
		length: 8
	}]
};
//#endregion
//#region src/types/sandbox-limits.ts
/**
* Global SANDBOX environment limits
* These limits apply to all customers in SANDBOX environment
*/
const SANDBOX_VERIFICATION_LIMIT = 150;
const SANDBOX_TIME_LIMIT_DAYS = 14;
//#endregion
//#region src/types/types.ts
const WebHookEventType = { VERIFICATION_SUCCEEDED: "VERIFICATION_SUCCEEDED" };
const MOBILE_REDIRECT = { CHANNEL: `innovatrics:rivaas:mobile_verification_process` };
const MobileRedirectStatus = {
	CONNECTED: "connected",
	FINISHED: "finished",
	ERROR: "error",
	EXPIRED: "expired"
};
//#endregion
//#region src/logger/logger.ts
var Logger = class Logger {
	pino;
	getSessionData;
	constructor(pino, getSessionData = void 0) {
		this.pino = pino;
		this.getSessionData = getSessionData;
	}
	fatal(obj, message) {
		this.pino.fatal(obj, message);
	}
	error(obj, message) {
		this.pino.error(obj, message);
	}
	warn(obj, message) {
		this.pino.warn(obj, message);
	}
	info(obj, message) {
		this.pino.info(obj, message);
	}
	debug(obj, message) {
		this.pino.debug(obj, message);
	}
	trace(obj, message) {
		this.pino.trace(obj, message);
	}
	withSessionData() {
		const sessionData = this.getSessionData?.() ?? {};
		return new Logger(this.pino.child(sessionData));
	}
};
//#endregion
//#region src/logger/pino-logger.ts
function initPinoLogger(options) {
	const destination = pino.destination(1);
	return pino({
		level: options.level || "info",
		formatters: {
			bindings: (bindings) => {
				return { pid: bindings.pid };
			},
			level: (label) => {
				return { level: label.toUpperCase() };
			}
		},
		timestamp: () => `,"timestamp":"${new Date(Date.now()).toISOString()}"`
	}, destination);
}
//#endregion
//#region src/format/url.ts
function removeTrailingSlash(url) {
	return url.endsWith("/") ? url.slice(0, -1) : url;
}
//#endregion
//#region src/http/sanitize.ts
function getOrganizationIdFromHeaders(headers) {
	const orgId = headers["x-org-id"];
	return Array.isArray(orgId) ? orgId[0] : orgId;
}
function sanitizeHeaders(headers) {
	const sanitized = {};
	Object.entries(headers).forEach(([key, value]) => {
		if (value !== void 0 && ![
			"authorization",
			"cookie",
			"x-api-key"
		].includes(key.toLowerCase())) sanitized[key] = value;
	});
	return sanitized;
}
function sanitizeBody(body) {
	if (typeof body !== "object" || body === null) return body;
	const sanitized = { ...body };
	[
		"password",
		"token",
		"secret",
		"key",
		"authorization"
	].forEach((field) => {
		if (field in sanitized) sanitized[field] = "[REDACTED]";
	});
	return sanitized;
}
//#endregion
//#region src/errors/UnauthorizedError.ts
var UnauthorizedError = class extends Error {
	status = 401;
	constructor(message = "Unauthorized") {
		super(message);
		this.name = "UnauthorizedError";
	}
};
//#endregion
//#region src/auth/auth-transfer.ts
/**
* Prenos prihlasenia z iného pôvodu (napr. dashboard -> onboarding-review) cez hash fragment
* (neodchádza v HTTP requeste na server).
*
* v2: len refresh + org; cieľový UI si dotiahne `user` + `organizations` cez `organization-token` a `user-info`.
*/
const RIVAAS_AUTH_TRANSFER_HASH_KEY = "rivaas-auth-transfer";
function isRivaasAuthTransferV2(x) {
	if (!x || typeof x !== "object") return false;
	const p = x;
	if (p.v !== 2) return false;
	if (typeof p.refresh_token !== "string" || !p.refresh_token) return false;
	if (typeof p.organization_id !== "string" || !p.organization_id) return false;
	return p.rememberMe === true || p.rememberMe === false;
}
function isOrg(x) {
	if (!x || typeof x !== "object") return false;
	const o = x;
	return typeof o.id === "string" && typeof o.name === "string" && typeof o.alias === "string" && typeof o.displayName === "string" && typeof o.isActive === "boolean";
}
function isRivaasAuthTransferV1(x) {
	if (!x || typeof x !== "object") return false;
	const p = x;
	if (p.v !== 1) return false;
	if (typeof p.token !== "string" || !p.token) return false;
	if (typeof p.organizationId !== "string" || !p.organizationId) return false;
	if (typeof p.rememberMe !== "boolean") return false;
	if (p.refreshToken !== null && typeof p.refreshToken !== "string") return false;
	if (!p.user || typeof p.user !== "object") return false;
	if (!isOrg(p.organization)) return false;
	if (!Array.isArray(p.organizations) || p.organizations.length < 1 || !p.organizations.every(isOrg)) return false;
	return true;
}
function utf8ToBase64Url(json) {
	return btoa(unescape(encodeURIComponent(json))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}
function base64UrlToUtf8(s) {
	let b64 = s.replace(/-/g, "+").replace(/_/g, "/");
	const pad = b64.length % 4;
	if (pad) b64 += "====".slice(0, 4 - pad);
	return decodeURIComponent(escape(atob(b64)));
}
function encodeRivaasAuthTransfer(p) {
	return utf8ToBase64Url(JSON.stringify(p));
}
/** @deprecated use encodeRivaasAuthTransfer */
const encodeRivaasAuthTransferPayload = encodeRivaasAuthTransfer;
function decodeRivaasAuthTransfer(encoded) {
	try {
		const raw = JSON.parse(base64UrlToUtf8(encoded));
		if (isRivaasAuthTransferV2(raw)) return raw;
		if (isRivaasAuthTransferV1(raw)) return raw;
		return null;
	} catch {
		return null;
	}
}
/** @deprecated use decodeRivaasAuthTransfer */
const decodeRivaasAuthTransferPayload = decodeRivaasAuthTransfer;
/**
* Očakáva hash v tvare #rivaas-auth-transfer=<base64url> alebo & (niektoré prehliadače)
*/
function parseRivaasAuthTransferFromHash(hash) {
	const h = hash.startsWith("#") ? hash.slice(1) : hash;
	const key = `${RIVAAS_AUTH_TRANSFER_HASH_KEY}=`;
	for (const part of h.split("&")) if (part.startsWith(key)) {
		const encoded = part.slice(key.length);
		if (encoded) return { encoded: decodeURIComponent(encoded) };
	}
	return null;
}
//#endregion
//#region src/palm.ts
/**
* SAIDVU-499/501 (Palm epic SAIDVU-302) — shared palm-capture runtime contract between the
* BFF palm step handlers, client UIs rendering the rejection, and the docs/error catalog.
*/
/** Hand sides in the platform's lowercase wire vocabulary (DIS speaks uppercase LEFT/RIGHT). */
const PALM_HANDS = ["left", "right"];
function otherPalmHand(hand) {
	return hand === "left" ? "right" : "left";
}
/**
* Why a palm capture was rejected (`PALM_CAPTURE_REJECTED.details.reason`). Locked 7-value
* enum from the WP-499 design note (idv-buildout-palm.md):
* - `dorsal_side` — back of the hand shown (FR-007)
* - `same_hand_again` — both_palms_capture already accepted this hand; the OTHER palm is expected (FR-006)
* - `no_palm` — DIS detected no palm / invalid capture (rotated, closed, non-palm object — FR-007)
* - `multiple_palms` — more than one palm in frame
* - `liveness_below_threshold` — palm liveness score under the tenant's `palm_liveness_threshold` TF (FR-005)
* - `injection_detected` — DIS video-injection inspection tripped (FR-003)
* - `classification_unknown` — hand position/orientation UNKNOWN or absent → invalid capture, forced retake (locked P4)
*/
const PALM_CAPTURE_REJECTION_REASONS = [
	"dorsal_side",
	"same_hand_again",
	"no_palm",
	"multiple_palms",
	"liveness_below_threshold",
	"injection_detected",
	"classification_unknown"
];
//#endregion
//#region src/trust-factor.ts
/**
* Trust-factor configurations the system seeds for every tenant. Protected from deletion —
* enforced server-side in config-service (`deleteTrustFactorConfig`) and reflected in the
* dashboard UI (Default badge + disabled delete). Single source of truth for both sides.
*
* Note: config-service's Prisma schema defaults `tf_customer_config.name` to the same value —
* keep that default in sync with `DEFAULT_TRUST_EVALUATION_CONFIG_NAME`.
*/
const DEFAULT_TRUST_EVALUATION_CONFIG_NAME = "default_trust_evaluation_configuration";
const AUTH_TRUST_CONFIG_NAME = "default_auth_trust_configuration";
/**
* SAIDVU-237. The two per-step configs `age_check_v1` selects with `params.trustFactorConfig`.
* Named to match the other platform-managed configurations
* (`default_trust_evaluation_configuration`, `default_auth_trust_configuration`) rather than the
* trust factors they enable — the dashboard lists all four together, so they have to read as a set.
*/
const AGE_ESTIMATION_TRUST_CONFIG_NAME = "default_age_estimation_trust_configuration";
const AGE_VERIFICATION_TRUST_CONFIG_NAME = "default_age_verification_trust_configuration";
const SYSTEM_TRUST_CONFIG_NAMES = /* @__PURE__ */ new Set([
	DEFAULT_TRUST_EVALUATION_CONFIG_NAME,
	AUTH_TRUST_CONFIG_NAME,
	AGE_ESTIMATION_TRUST_CONFIG_NAME,
	AGE_VERIFICATION_TRUST_CONFIG_NAME
]);
/**
* Evaluated straight after the selfie, before any document exists, so every document factor stays
* off — enabling one could only produce `MISSING_TRUST_FACTOR_DATA`.
*
* `rejectLow` 25 (epic A2) is a safety margin over a typical 18 limit that absorbs estimator
* error. It rejects rather than reviews, so a genuine 20-year-old is turned away here — tolerable
* only because the next step settles the age from the date of birth, and not on a terminal check.
*/
const AGE_ESTIMATION_TRUST_CONFIG_OVERLAY = {
	name: AGE_ESTIMATION_TRUST_CONFIG_NAME,
	enabled: {
		age_estimation: {
			rejectedLowScoreValue: 25,
			reviewLowScoreValue: null
		},
		face_not_covered: {},
		passive_liveness: {},
		video_injection: {}
	}
};
/**
* Evaluated after the document capture, so it carries the document authenticity and
* face-to-document checks alongside the date-of-birth age.
*
* `age_validation` is on here and nowhere else: it scores the discrepancy between the estimated
* and document ages, so it needs both to mean anything.
*/
const AGE_VERIFICATION_TRUST_CONFIG_OVERLAY = {
	name: AGE_VERIFICATION_TRUST_CONFIG_NAME,
	enabled: {
		age_validation: {},
		age_verification: {
			rejectedLowScoreValue: 18,
			reviewLowScoreValue: null
		},
		document_classification_both_pages_present: {},
		document_classification_not_level_1: {},
		document_face_match: {},
		document_liveness: {},
		document_not_expired: {},
		document_portrait_genuine: {},
		document_portrait_replicated: {},
		document_print_copy: {},
		face_not_covered: {},
		mrz_checksum_valid: {},
		passive_liveness: {},
		video_injection: {}
	}
};
/**
* SAIDVU-237. The two factors that gate on a legal age. They score an age in years and must
* reject *below* a threshold, so a high-band threshold inverts them: with `rejectedHigh: 20` DIS
* rejects adults and passes a 16-year-old, and the validator would otherwise accept that
* silently. `age_validation` is deliberately absent — it scores a discrepancy and its thresholds
* legitimately belong to the high band.
*/
const AGE_GATE_TRUST_FACTOR_NAMES = ["age_estimation", "age_verification"];
const AGE_GATE_TRUST_FACTOR_NAME_SET = new Set(AGE_GATE_TRUST_FACTOR_NAMES);
const AGE_TRUST_CONFIG_OVERLAYS = [AGE_ESTIMATION_TRUST_CONFIG_OVERLAY, AGE_VERIFICATION_TRUST_CONFIG_OVERLAY];
/**
* SAIDVU-502 / FR-021 — the two tenant-configurable Palm trust factors (palm epic
* SAIDVU-302). Shared contract between config-service (seed + FR-002 gating by the
* SAIDVU-498 palm flag), the BFF palm capture step (WP-499 reads
* `palm_liveness_threshold`), the temporal `palm_matching` activity (WP-501 reads
* `palm_matching_score_threshold`) and the dashboard Palm TF section (WP-504).
* Consumers fetch them like any other factor: `GET /api/v1/trust-factor/{org}`
* (optionally `?trustFactorNames=`) or `/by-name/{config}` — config-service omits them
* for palm-off tenants.
*/
const PALM_TRUST_FACTOR_NAMES = ["palm_liveness_threshold", "palm_matching_score_threshold"];
const PALM_TRUST_FACTOR_NAME_SET = new Set(PALM_TRUST_FACTOR_NAMES);
//#endregion
//#region src/workflow/schema.ts
/**
* Single source of truth for workflow manifest IDs (steps + outcome actions).
* UI, BFF, and runtime validation must import these — do not duplicate lists.
*/
const WORKFLOW_STEP_ACTIONS = [
	"mobile_redirect",
	"document_capture",
	"face_capture",
	"smile_capture",
	"multirange_capture",
	"nfc_capture",
	"external_capture",
	"tf_eval",
	"dedup_face",
	"dedup_document",
	"best_duplicate",
	"matching",
	"decision",
	"both_palms_capture",
	"single_palm_capture",
	"palm_matching"
];
/** Human-readable labels for dashboard (and optionally BFF tooling). */
const WORKFLOW_STEP_ACTION_LABELS = {
	mobile_redirect: "Mobile redirect",
	document_capture: "Document capture",
	face_capture: "Face capture",
	smile_capture: "Smile capture",
	multirange_capture: "Multirange capture",
	nfc_capture: "NFC capture",
	external_capture: "External capture",
	tf_eval: "Trust factor eval",
	dedup_face: "Face dedup",
	dedup_document: "Document dedup",
	best_duplicate: "Best duplicate",
	matching: "Matching",
	decision: "Decision",
	both_palms_capture: "Both palms capture",
	single_palm_capture: "Single palm capture",
	palm_matching: "Palm matching"
};
const WORKFLOW_PARAM_LESS_STEP_ACTIONS = [
	"face_capture",
	"smile_capture",
	"multirange_capture",
	"nfc_capture",
	"dedup_document",
	"both_palms_capture",
	"single_palm_capture"
];
/**
* SAIDVU-498 / FR-001–002 — the three D39 palm step actions. A manifest containing any of
* these is only storable (config-service) and runnable (BFF session) for tenants whose
* `connection_settings.palm_enabled` flag is on.
*/
const WORKFLOW_PALM_STEP_ACTIONS = [
	"both_palms_capture",
	"single_palm_capture",
	"palm_matching"
];
/**
* Palm-feature gate predicate (SAIDVU-498). Takes bare `{ action }` objects so raw manifest
* JSON (config-service DB rows) and validated `WorkflowStep[]` both fit — same contract as
* `workflowRequiresCustomerId`.
*/
function workflowContainsPalmSteps(steps) {
	const palmActions = new Set(WORKFLOW_PALM_STEP_ACTIONS);
	return steps.some((step) => palmActions.has(step.action));
}
/**
* Subset of step actions that drive UI capture phases — handled by BFF runtime.
* Other step actions (tf_eval, dedup_*, matching, decision) are processed by
* the Temporal worker after BFF hands off the captures.
*/
const WORKFLOW_UI_STEP_ACTIONS = [
	"mobile_redirect",
	"document_capture",
	"face_capture",
	"smile_capture",
	"multirange_capture",
	"nfc_capture",
	"external_capture",
	"both_palms_capture",
	"single_palm_capture"
];
/**
* Step actions that take a `params.trustFactorConfig`. This is also the set config-service
* treats as a REFERENCE to a trust-factor config — it gates deletion — so a step listed here
* that never reads the value would block tenants from deleting a config they don't use.
*
* `dedup_document` is deliberately absent: its thresholds (matchingThreshold /
* similarThreshold / dobRange) come from the tenant's dedup-document config, never from a
* trust factor.
*/
const WORKFLOW_TRUST_FACTOR_STEP_ACTIONS = [
	"tf_eval",
	"dedup_face",
	"matching",
	"palm_matching"
];
/**
* A `matching` / `palm_matching` step authenticates the session against an EXISTING
* Customer, so session creation must receive a `customer_id` to match against. BFF
* (POST /session `CUSTOMER_ID_REQUIRED` validation) and config-service (the
* workflow-list `requiresCustomerId` flag that client UIs gate their customer-id
* input on) must both derive the rule from this predicate. For `palm_matching` the
* customer id additionally feeds the FR-019 palm-record-exists guard at session init.
*
* Takes bare `{ action }` objects so raw manifest JSON (config-service DB rows)
* and validated `WorkflowStep[]` both fit.
*/
function workflowRequiresCustomerId(steps) {
	return steps.some((step) => step.action === "matching" || step.action === "palm_matching");
}
/**
* Step actions that run inside Temporal `BackendSegmentWorkflow` (not BFF UI runtime).
* BFF uses this to segment a manifest's `steps[]` into UI vs temporal contiguous blocks
* and dispatch one workflow execution per temporal block.
*
* = trust-factor set ∪ `dedup_document`, `best_duplicate`, `decision`. Where a step runs and
* what params it takes are independent axes, so the extras are listed explicitly rather than
* derived: `decision` takes CEL `rules`, and `dedup_document` / `best_duplicate` take no
* params at all.
*
* Fallback constant until the manifest schema gains an explicit `executor` field.
*/
const WORKFLOW_TEMPORAL_STEP_ACTIONS = [
	...WORKFLOW_TRUST_FACTOR_STEP_ACTIONS,
	"dedup_document",
	"best_duplicate",
	"decision"
];
/**
* Decision-rule "result" signals: source step action -> allowed result values.
* Used by both the validator (to reject unknown clauses in `when`) and the
* dashboard builder (to render compact result/value pickers).
*/
const WORKFLOW_DECISION_RESULT_SIGNALS = {
	tf_eval: [
		"accept",
		"review",
		"reject"
	],
	dedup_face: [
		"no_match",
		"customer",
		"blocklist",
		"concurrent",
		"review"
	],
	dedup_document: [
		"no_match",
		"customer",
		"blocklist",
		"concurrent",
		"review"
	],
	matching: ["match", "no_match"],
	palm_matching: ["match", "no_match"],
	document_capture: ["ocr_quality_failed"]
};
/**
* SAIDVU-15 story 2.4 — `best_duplicate` decision signals. Unlike the dedup steps the
* referenceable keys are `.outcome` and `.type` (not `.result`), so they live outside
* `WORKFLOW_DECISION_RESULT_SIGNALS` (whose generic clause form is `<source>.result`).
* `no_match` is the stable empty-state value (the dedup-step precedent); the other six
* are the spec-14 truth-table outcomes — double-s spellings are the wire contract.
*/
const BEST_DUPLICATE_OUTCOMES = [
	"no_match",
	"full_hit",
	"similar_face",
	"similar_name",
	"face_missmatch",
	"doc_missmatch",
	"multiple_full_hits"
];
/**
* Winner's watchlist mapped to a type. NOTE: spec-14 lists only customer|blocklist|
* concurrent — `review` is carried for review-watchlist winners (open PO question,
* consistent with the AD-4 ruling that review hits stay representable).
*/
const BEST_DUPLICATE_TYPES = [
	"customer",
	"blocklist",
	"concurrent",
	"review"
];
/**
* Workflow terminal states (D16). A decision rule (or default) that sets
* `terminate` to one of these ends workflow execution in that state. Omitting
* `terminate` (or the empty-string fallthrough sentinel on a `default`) lets the
* workflow continue to the next step. Mirrors the canonical `TerminalState`
* `$def` in `claude-docs/product-spec/workflow-manifest.schema.json`.
*/
const WORKFLOW_TERMINAL_STATES = [
	"accept",
	"reject",
	"review",
	"incomplete"
];
const WORKFLOW_OUTCOME_ACTIONS = [
	"create_new_customer",
	"create_blocked_customer",
	"update_identified_customer",
	"repoint_primary_DI",
	"add_new_DI_to_customer",
	"1N_add_current_to_watchlist",
	"1N_add_current_to_blocklist",
	"1N_move_to_review",
	"reject_concurrents",
	"merge_rejected_concurrents",
	"emit_event"
];
const WORKFLOW_ACTIONS_WITH_EXTERNAL_ID_TEMPLATE = ["create_new_customer", "create_blocked_customer"];
const WORKFLOW_EXTERNAL_ID_TEMPLATE_VARS = [
	"uuid",
	"document.personal_number",
	"document.document_number"
];
//#endregion
//#region src/workflow/validator.ts
const SNAKE_CASE_REGEX = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;
function isRecord$1(value) {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isNonEmptyString(value) {
	return typeof value === "string" && value.trim().length > 0;
}
function push(issues, path, message) {
	issues.push({
		path,
		message
	});
}
const TERMINAL_STATES = new Set(WORKFLOW_TERMINAL_STATES);
const TERMINAL_STATES_HINT = WORKFLOW_TERMINAL_STATES.map((s) => `'${s}'`).join(", ");
/**
* Validates a `terminate` value against the D16 terminal-state vocabulary.
*
* - On a rule, `terminate` is required and must be a valid terminal state.
* - On a `default`, the empty-string fallthrough sentinel is allowed (the
*   workflow continues to the next step); any non-empty value must be a valid
*   terminal state.
*/
function validateTerminate(value, issues, path, { allowEmpty }) {
	if (allowEmpty && value === "") return;
	if (!isNonEmptyString(value)) {
		push(issues, path, allowEmpty ? "Expected string." : "Expected non-empty string.");
		return;
	}
	if (!TERMINAL_STATES.has(value)) push(issues, path, `Expected one of ${TERMINAL_STATES_HINT}.`);
}
function validateNoUnknownKeys(value, allowedKeys, issues, path) {
	if (!isRecord$1(value)) return;
	const allowed = new Set(allowedKeys);
	for (const key of Object.keys(value)) if (!allowed.has(key)) push(issues, `${path}.${key}`, `Unknown key '${key}'.`);
}
/**
* SAIDVU-15 story 2.3 hard cutover: retired identifiers are rejected with the exact
* replacement named, so a failed save tells the author (or the migration operator)
* precisely what to change instead of a generic "unknown".
*/
const RENAMED_STEP_ACTIONS = { dedup_facial: "dedup_face" };
const RENAMED_DEDUP_RESULT_VALUES = {
	fraud: "blocklist",
	hits: "customer",
	pending_review: "review"
};
/** Old dedup_document placeholders with NO rename — authors must re-model the rule. */
const RETIRED_DEDUP_RESULT_VALUES = /* @__PURE__ */ new Set(["full_match", "similar_match"]);
const DEDUP_RESULT_SOURCES = /* @__PURE__ */ new Set(["dedup_document", "dedup_face"]);
function validateStepAction(action, issues, path) {
	if (!isNonEmptyString(action)) {
		push(issues, path, "Expected non-empty string.");
		return false;
	}
	if (!WORKFLOW_STEP_ACTIONS.includes(action)) {
		const replacement = RENAMED_STEP_ACTIONS[action];
		push(issues, path, replacement ? `Unknown action '${action}' — renamed to '${replacement}' (SAIDVU-15 dedup cutover).` : `Unknown action '${action}'.`);
		return false;
	}
	return true;
}
const TEMPLATE_TOKEN_REGEX = /\$\{([a-zA-Z0-9_.]+)\}/g;
const EXTERNAL_ID_TEMPLATE_VARS = new Set(WORKFLOW_EXTERNAL_ID_TEMPLATE_VARS);
const ACTIONS_WITH_EXTERNAL_ID_TEMPLATE = new Set(WORKFLOW_ACTIONS_WITH_EXTERNAL_ID_TEMPLATE);
/**
* D32 authoring-time check for `params.external_id_template` (SAIDVU-522/523): the value
* must be a non-empty string, and every `${...}` token inside it must resolve against the
* `WORKFLOW_EXTERNAL_ID_TEMPLATE_VARS` allowlist (D32 "resolvable variable references").
* Runtime rendering + the unresolved-value failure path live in the Temporal worker.
*/
function validateExternalIdTemplateParams(params, issues, path) {
	if (!isRecord$1(params)) {
		push(issues, path, "Expected object.");
		return;
	}
	validateNoUnknownKeys(params, ["external_id_template"], issues, path);
	const template = params.external_id_template;
	const templatePath = `${path}.external_id_template`;
	if (!isNonEmptyString(template)) {
		push(issues, templatePath, "Expected non-empty string.");
		return;
	}
	for (const match of template.matchAll(TEMPLATE_TOKEN_REGEX)) {
		const variable = match[1];
		if (!EXTERNAL_ID_TEMPLATE_VARS.has(variable)) push(issues, templatePath, `Unknown template variable '${variable}'.`);
	}
}
function validateOutcomeActionEntry(action, issues, path) {
	if (!isNonEmptyString(action)) {
		push(issues, path, "Expected non-empty string.");
		return;
	}
	if (!WORKFLOW_OUTCOME_ACTIONS.includes(action)) push(issues, path, `Unknown outcome action '${action}'.`);
}
function validateMeta(meta, issues, path) {
	if (!isRecord$1(meta)) {
		push(issues, path, "Expected object.");
		return;
	}
	validateNoUnknownKeys(meta, ["label"], issues, path);
	if (!isNonEmptyString(meta.label)) push(issues, `${path}.label`, "Expected non-empty string.");
}
function validateOutcomes(outcomes, issues, path) {
	if (!isRecord$1(outcomes)) {
		push(issues, path, "Expected object map.");
		return;
	}
	for (const [key, value] of Object.entries(outcomes)) {
		if (!SNAKE_CASE_REGEX.test(key)) push(issues, `${path}.${key}`, `Expected snake_case outcome id, got '${key}'.`);
		const outcomePath = `${path}.${key}`;
		if (!isRecord$1(value)) {
			push(issues, outcomePath, "Expected object.");
			continue;
		}
		validateNoUnknownKeys(value, ["actions"], issues, outcomePath);
		const actions = value.actions;
		if (!Array.isArray(actions)) {
			push(issues, `${outcomePath}.actions`, "Expected array.");
			continue;
		}
		for (let i = 0; i < actions.length; i++) {
			const a = actions[i];
			const actionPath = `${outcomePath}.actions[${i}]`;
			if (!isRecord$1(a)) {
				push(issues, actionPath, "Expected object.");
				continue;
			}
			const actionEligibleForTemplate = isNonEmptyString(a.action) && ACTIONS_WITH_EXTERNAL_ID_TEMPLATE.has(a.action);
			validateNoUnknownKeys(a, actionEligibleForTemplate ? ["action", "params"] : ["action"], issues, actionPath);
			validateOutcomeActionEntry(a.action, issues, `${actionPath}.action`);
			if (actionEligibleForTemplate && "params" in a) validateExternalIdTemplateParams(a.params, issues, `${actionPath}.params`);
		}
		const actionNames = actions.map((a) => isRecord$1(a) && isNonEmptyString(a.action) ? a.action : null);
		const mergeIdx = actionNames.indexOf("merge_rejected_concurrents");
		const rejectIdx = actionNames.indexOf("reject_concurrents");
		const createIdx = actionNames.indexOf("create_new_customer");
		if (mergeIdx !== -1 && rejectIdx !== -1) push(issues, `${outcomePath}.actions`, "`reject_concurrents` and `merge_rejected_concurrents` are mutually exclusive.");
		if (mergeIdx !== -1 && !(createIdx !== -1 && createIdx < mergeIdx)) push(issues, `${outcomePath}.actions[${mergeIdx}].action`, "`merge_rejected_concurrents` requires `create_new_customer` earlier in the same outcome.");
	}
}
function validateDecisionParams(params, issues, path, outcomesKeys, precedingStepActions) {
	if (!isRecord$1(params)) {
		push(issues, path, "Expected object.");
		return;
	}
	validateNoUnknownKeys(params, ["rules", "default"], issues, path);
	const rules = params.rules;
	if (!Array.isArray(rules) || rules.length === 0) push(issues, `${path}.rules`, "Expected non-empty array.");
	else for (let i = 0; i < rules.length; i++) {
		const r = rules[i];
		const rulePath = `${path}.rules[${i}]`;
		if (!isRecord$1(r)) {
			push(issues, rulePath, "Expected object.");
			continue;
		}
		validateNoUnknownKeys(r, [
			"when",
			"outcome",
			"terminate",
			"reason"
		], issues, rulePath);
		if (!isNonEmptyString(r.when)) push(issues, `${rulePath}.when`, "Expected non-empty string.");
		else {
			const parts = r.when.split("&&").map((x) => x.trim()).filter(Boolean);
			const clauseIssue = (clause) => {
				if (/^(dedup_document)\.hit\s*(==|!=)\s*(dedup_face)\.hit$/.test(clause)) return null;
				const bestDuplicate = clause.match(/^best_duplicate\.(outcome|type)\s*==\s*'([^']+)'$/);
				if (bestDuplicate) {
					const field = bestDuplicate[1] ?? "";
					const value = bestDuplicate[2] ?? "";
					const allowed = field === "outcome" ? BEST_DUPLICATE_OUTCOMES : BEST_DUPLICATE_TYPES;
					if (allowed.includes(value)) return null;
					return `Unknown value '${value}' for 'best_duplicate.${field}'. Allowed: ${allowed.join(", ")}.`;
				}
				if (/dedup_facial\.hit/.test(clause)) return "'dedup_facial.hit' was renamed — use dedup_document.hit == dedup_face.hit (SAIDVU-15 dedup cutover).";
				const res = clause.match(/^([a-z_]+)\.result\s*==\s*'([^']+)'$/);
				if (!res) return "Unsupported expression. Use '&&' with allowed clauses like tf_eval.result == 'accept'.";
				const source = res[1] ?? "";
				const value = res[2] ?? "";
				if (source === "dedup_facial") return "Source 'dedup_facial' was renamed to 'dedup_face' (SAIDVU-15 dedup cutover).";
				const allowed = WORKFLOW_DECISION_RESULT_SIGNALS[source];
				if (!allowed) return "Unsupported expression. Use '&&' with allowed clauses like tf_eval.result == 'accept'.";
				if (allowed.includes(value)) return null;
				if (DEDUP_RESULT_SOURCES.has(source)) {
					const replacement = RENAMED_DEDUP_RESULT_VALUES[value];
					if (replacement) return `Result value '${value}' was renamed to '${replacement}' for '${source}' (SAIDVU-15 dedup cutover).`;
					if (RETIRED_DEDUP_RESULT_VALUES.has(value)) return `Result value '${value}' was removed — branch on the 5-value vocabulary (${allowed.join("|")}) instead (SAIDVU-15 dedup cutover).`;
				}
				return `Unknown result value '${value}' for '${source}'. Allowed: ${allowed.join(", ")}.`;
			};
			const extractSources = (clause) => {
				const hit = clause.match(/^(dedup_document)\.hit\s*(==|!=)\s*(dedup_face)\.hit$/);
				if (hit) return [hit[1], hit[3]];
				if (/^best_duplicate\.(outcome|type)\s*==\s*'[^']+'$/.test(clause)) return ["best_duplicate"];
				const res = clause.match(/^([a-z_]+)\.result\s*==\s*'([^']+)'$/);
				if (res) return [res[1]];
				return [];
			};
			const clauseIssues = parts.map((p) => clauseIssue(p)).filter((m) => m !== null);
			if (!parts.length) push(issues, `${rulePath}.when`, "Unsupported expression. Use '&&' with allowed clauses like tf_eval.result == 'accept'.");
			else if (clauseIssues.length > 0) for (const message of clauseIssues) push(issues, `${rulePath}.when`, message);
			else if (precedingStepActions) {
				for (const clause of parts) for (const source of extractSources(clause)) if (!precedingStepActions.has(source)) push(issues, `${rulePath}.when`, `Source '${source}' does not match any preceding step action in the manifest.`);
				else if ((precedingStepActions.get(source) ?? 0) > 1) push(issues, `${rulePath}.when`, `Ambiguous source '${source}': multiple steps with that action precede this decision. The decision engine cannot determine which step is referenced.`);
			}
		}
		if (!isNonEmptyString(r.outcome)) push(issues, `${rulePath}.outcome`, "Expected non-empty string.");
		validateTerminate(r.terminate, issues, `${rulePath}.terminate`, { allowEmpty: false });
		if ("reason" in r && r.reason !== void 0 && !isNonEmptyString(r.reason)) push(issues, `${rulePath}.reason`, "Expected non-empty string when provided.");
		if (outcomesKeys && typeof r.outcome === "string" && !outcomesKeys.has(r.outcome)) push(issues, `${rulePath}.outcome`, `Unknown outcome '${r.outcome}'.`);
	}
	if ("default" in params && params.default !== void 0) {
		const d = params.default;
		const defPath = `${path}.default`;
		if (!isRecord$1(d)) push(issues, defPath, "Expected object.");
		else {
			if (!isNonEmptyString(d.outcome)) push(issues, `${defPath}.outcome`, "Expected non-empty string.");
			validateTerminate(d.terminate, issues, `${defPath}.terminate`, { allowEmpty: true });
			if (outcomesKeys && typeof d.outcome === "string" && !outcomesKeys.has(d.outcome)) push(issues, `${defPath}.outcome`, `Unknown outcome '${d.outcome}'.`);
		}
	}
}
function validateStep(step, issues, path, outcomesKeys, precedingStepActions) {
	if (!isRecord$1(step)) {
		push(issues, path, "Expected object.");
		return;
	}
	validateNoUnknownKeys(step, [
		"order",
		"name",
		"action",
		"params"
	], issues, path);
	const order = step.order;
	if (typeof order !== "number" || !Number.isFinite(order)) push(issues, `${path}.order`, "Expected number.");
	if (!isNonEmptyString(step.name)) push(issues, `${path}.name`, "Expected non-empty string.");
	const action = validateStepAction(step.action, issues, `${path}.action`) ? step.action : null;
	if ("params" in step && step.params !== void 0 && !isRecord$1(step.params)) push(issues, `${path}.params`, "Expected object when provided.");
	if (action === "decision") {
		validateDecisionParams(step.params, issues, `${path}.params`, outcomesKeys, precedingStepActions);
		return;
	}
	if (action === "mobile_redirect") {
		if (step.params === void 0) return;
		const params = step.params;
		validateNoUnknownKeys(params, ["skippable"], issues, `${path}.params`);
		if (isRecord$1(params) && "skippable" in params && params.skippable !== void 0 && typeof params.skippable !== "boolean") push(issues, `${path}.params.skippable`, "Expected boolean when provided.");
		return;
	}
	if (action === "document_capture") {
		if (!isRecord$1(step.params)) {
			push(issues, `${path}.params`, "Expected object.");
			return;
		}
		validateNoUnknownKeys(step.params, [
			"allowedDocuments",
			"backPageSkippable",
			"captureBackPage"
		], issues, `${path}.params`);
		if (!Array.isArray(step.params.allowedDocuments) || step.params.allowedDocuments.length === 0) push(issues, `${path}.params.allowedDocuments`, "Expected non-empty array.");
		else for (let i = 0; i < step.params.allowedDocuments.length; i++) {
			const d = step.params.allowedDocuments[i];
			const docPath = `${path}.params.allowedDocuments[${i}]`;
			if (!isRecord$1(d)) {
				push(issues, docPath, "Expected object.");
				continue;
			}
			validateNoUnknownKeys(d, [
				"type",
				"countries",
				"allCountries"
			], issues, docPath);
			if (!isNonEmptyString(d.type)) push(issues, `${docPath}.type`, "Expected non-empty string.");
			if (!Array.isArray(d.countries) || d.countries.some((c) => typeof c !== "string")) push(issues, `${docPath}.countries`, "Expected string array.");
			const allCountriesRaw = d.allCountries;
			const allCountries = allCountriesRaw === true;
			if ("allCountries" in d && allCountriesRaw !== void 0 && typeof allCountriesRaw !== "boolean") push(issues, `${docPath}.allCountries`, "Expected boolean when provided.");
			if (Array.isArray(d.countries)) {
				const hasCountries = d.countries.length > 0;
				if (allCountries && hasCountries) push(issues, `${docPath}.countries`, "Must be empty array when allCountries is true (use countries: []).");
				if (!allCountries && !hasCountries) push(issues, `${docPath}.countries`, "Expected non-empty array unless allCountries is true.");
			}
		}
		if (typeof step.params.captureBackPage !== "boolean") push(issues, `${path}.params.captureBackPage`, "Expected boolean.");
		if (step.params.backPageSkippable !== void 0 && typeof step.params.backPageSkippable !== "boolean") push(issues, `${path}.params.backPageSkippable`, "Expected boolean when provided.");
		return;
	}
	if (action !== null && WORKFLOW_TRUST_FACTOR_STEP_ACTIONS.includes(action)) {
		if (!isRecord$1(step.params)) {
			push(issues, `${path}.params`, "Expected object.");
			return;
		}
		validateNoUnknownKeys(step.params, ["trustFactorConfig"], issues, `${path}.params`);
		if (!isNonEmptyString(step.params.trustFactorConfig)) push(issues, `${path}.params.trustFactorConfig`, "Expected non-empty string.");
		return;
	}
	if (action === "best_duplicate") {
		if (step.params === void 0) return;
		if (isRecord$1(step.params)) {
			validateNoUnknownKeys(step.params, [], issues, `${path}.params`);
			return;
		}
		push(issues, `${path}.params`, "Expected object when provided.");
		return;
	}
	if (action !== null && WORKFLOW_PARAM_LESS_STEP_ACTIONS.includes(action)) {
		if (step.params === void 0) return;
		if (isRecord$1(step.params)) {
			validateNoUnknownKeys(step.params, [], issues, `${path}.params`);
			return;
		}
		push(issues, `${path}.params`, "Expected object when provided.");
	}
}
function validateWorkflow(workflow) {
	const issues = [];
	if (!isRecord$1(workflow)) return {
		ok: false,
		issues: [{
			path: "$",
			message: "Expected object."
		}]
	};
	validateNoUnknownKeys(workflow, [
		"version",
		"workflowName",
		"meta",
		"steps",
		"outcomes"
	], issues, "$");
	if (typeof workflow.version !== "number" || !Number.isFinite(workflow.version)) push(issues, "$.version", "Expected number.");
	if (!isNonEmptyString(workflow.workflowName)) push(issues, "$.workflowName", "Expected non-empty string.");
	validateMeta(workflow.meta, issues, "$.meta");
	const steps = workflow.steps;
	if (!Array.isArray(steps) || steps.length === 0) push(issues, "$.steps", "Expected non-empty array.");
	const outcomes = workflow.outcomes;
	const legacyOutcomes = workflow.Outcomes;
	if (legacyOutcomes !== void 0 && outcomes === void 0) push(issues, "$.outcomes", "Expected camelCase key \"outcomes\" (not \"Outcomes\").");
	const outcomesValue = outcomes ?? legacyOutcomes;
	const outcomesKeys = isRecord$1(outcomesValue) ? new Set(Object.keys(outcomesValue)) : null;
	validateOutcomes(outcomesValue, issues, "$.outcomes");
	if (Array.isArray(steps)) {
		const orders = /* @__PURE__ */ new Set();
		const sortedSteps = [...steps.map((s, idx) => ({
			s,
			idx
		}))].sort((a, b) => {
			return (isRecord$1(a.s) && typeof a.s.order === "number" ? a.s.order : Infinity) - (isRecord$1(b.s) && typeof b.s.order === "number" ? b.s.order : Infinity);
		});
		let segmentCounts = /* @__PURE__ */ new Map();
		let hasDecision = false;
		let hasPrecedingDocumentCapture = false;
		for (let sortedIndex = 0; sortedIndex < sortedSteps.length; sortedIndex++) {
			const { idx, s } = sortedSteps[sortedIndex];
			const stepPath = `$.steps[${idx}]`;
			const isDecision = isRecord$1(s) && s.action === "decision";
			if (isDecision) hasDecision = true;
			if (isRecord$1(s) && s.action === "nfc_capture" && !hasPrecedingDocumentCapture) push(issues, `${stepPath}.action`, "Step action 'nfc_capture' requires a preceding 'document_capture' step in the same workflow.");
			if (isRecord$1(s) && s.action === "document_capture") hasPrecedingDocumentCapture = true;
			validateStep(s, issues, stepPath, outcomesKeys, isDecision ? segmentCounts : null);
			if (isRecord$1(s) && s.action === "mobile_redirect" && sortedIndex !== 0) push(issues, `${stepPath}.action`, "Step action 'mobile_redirect' must be first when present.");
			if (isRecord$1(s) && typeof s.order === "number" && Number.isFinite(s.order)) {
				if (orders.has(s.order)) push(issues, `${stepPath}.order`, `Duplicate order '${s.order}'.`);
				orders.add(s.order);
			}
			if (isDecision) segmentCounts = /* @__PURE__ */ new Map();
			else if (isRecord$1(s) && typeof s.action === "string") segmentCounts.set(s.action, (segmentCounts.get(s.action) ?? 0) + 1);
		}
		if (!hasDecision) push(issues, "$.steps", "Expected at least one decision step so the workflow can terminate.");
	}
	return issues.length ? {
		ok: false,
		issues
	} : { ok: true };
}
//#endregion
//#region src/workflow/migrate-dedup-vocabulary.ts
/**
* SAIDVU-15 story 2.3 — the one-shot dedup-vocabulary cutover rewrite.
*
* Pure string renames on a manifest (used by the config-service migration script and
* unit-testable without a DB): step action `dedup_facial → dedup_face`, when-clause
* source references `dedup_facial.result / dedup_facial.hit → dedup_face.*`, and the
* result literals `hits → customer`, `fraud → blocklist`, `pending_review → review` —
* SCOPED to dedup sources only (tf_eval/matching vocabularies and tenant-named outcome
* labels are never touched). dedup_document's retired `similar_match` / `full_match`
* placeholders have no defined rename: they are surfaced in `notes` for manual review,
* never rewritten. Idempotent: a migrated manifest round-trips unchanged.
*/
const RESULT_LITERAL_RENAMES = [
	["hits", "customer"],
	["fraud", "blocklist"],
	["pending_review", "review"]
];
const UNMAPPABLE_DOCUMENT_LITERALS = ["similar_match", "full_match"];
/** Sources whose result literals participate in the rename (the shared AD-4 vocabulary). */
const DEDUP_SOURCES_PATTERN = "(?:dedup_face|dedup_facial|dedup_document)";
function isObject(value) {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}
function rewriteWhenClause(when, notes, where) {
	let out = when;
	out = out.replace(/\bdedup_facial\.(result|hit)\b/g, "dedup_face.$1");
	for (const [oldValue, newValue] of RESULT_LITERAL_RENAMES) out = out.replace(new RegExp(`(${DEDUP_SOURCES_PATTERN}\\.result\\s*==\\s*)'${oldValue}'`, "g"), `$1'${newValue}'`);
	for (const literal of UNMAPPABLE_DOCUMENT_LITERALS) if (new RegExp(`${DEDUP_SOURCES_PATTERN}\\.result\\s*==\\s*'${literal}'`).test(out)) notes.push(`${where}: '${literal}' has no rename in the 5-value vocabulary (no_match|customer|blocklist|concurrent|review) — needs manual review.`);
	return out;
}
function migrateDedupVocabulary(input) {
	const manifest = structuredClone(input);
	const notes = [];
	let changed = false;
	const steps = isObject(manifest) && Array.isArray(manifest.steps) ? manifest.steps : [];
	for (const step of steps) {
		if (!isObject(step)) continue;
		if (step.action === "dedup_facial") {
			step.action = "dedup_face";
			changed = true;
		}
		if (step.action === "dedup_document" && isObject(step.params) && "trustFactorConfig" in step.params) {
			delete step.params.trustFactorConfig;
			if (Object.keys(step.params).length === 0) delete step.params;
			changed = true;
		}
		const params = isObject(step.params) ? step.params : void 0;
		const rules = params && Array.isArray(params.rules) ? params.rules : [];
		for (let i = 0; i < rules.length; i++) {
			const rule = rules[i];
			if (!isObject(rule) || typeof rule.when !== "string") continue;
			const where = `steps[${String(step.order ?? "?")}].rules[${i}]`;
			const rewritten = rewriteWhenClause(rule.when, notes, where);
			if (rewritten !== rule.when) {
				rule.when = rewritten;
				changed = true;
			}
		}
	}
	return {
		changed,
		manifest,
		notes
	};
}
//#endregion
//#region src/workflow/normalize.ts
const WORKFLOW_DECISION_DEFAULT = {
	outcome: "fallthrough",
	terminate: ""
};
const WORKFLOW_FALLTHROUGH_OUTCOME = { actions: [] };
function isRecord(value) {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}
function withFallthroughOutcome(outcomes) {
	if (isRecord(outcomes.fallthrough)) return outcomes;
	return {
		...outcomes,
		fallthrough: { ...WORKFLOW_FALLTHROUGH_OUTCOME }
	};
}
function withDecisionDefault(params) {
	if ("default" in params && params.default !== void 0 && params.default !== null) return params;
	return {
		...params,
		default: { ...WORKFLOW_DECISION_DEFAULT }
	};
}
/**
* Ensures every decision step has an explicit default and that the manifest
* defines the fallthrough outcome used by that default.
*/
function normalizeWorkflow(workflow) {
	if (!isRecord(workflow)) return workflow;
	const normalized = JSON.parse(JSON.stringify(workflow));
	normalized.outcomes = withFallthroughOutcome(isRecord(normalized.outcomes) ? normalized.outcomes : {});
	const steps = normalized.steps;
	if (!Array.isArray(steps)) return normalized;
	for (const step of steps) {
		if (!isRecord(step) || step.action !== "decision") continue;
		if (!isRecord(step.params)) {
			step.params = {
				default: { ...WORKFLOW_DECISION_DEFAULT },
				rules: []
			};
			continue;
		}
		step.params = withDecisionDefault(step.params);
	}
	return normalized;
}
//#endregion
//#region src/workflow/catalog.ts
const DEFAULT_WORKFLOWS = {
	ageCheckV1: {
		version: 1,
		workflowName: "age_check_v1",
		meta: { "label": "Age check" },
		steps: [
			{
				"order": 1,
				"name": "Mobile redirect",
				"action": "mobile_redirect",
				"params": { "skippable": true }
			},
			{
				"order": 2,
				"name": "Selfie",
				"action": "face_capture"
			},
			{
				"order": 3,
				"name": "Age estimation",
				"action": "tf_eval",
				"params": { "trustFactorConfig": "default_age_estimation_trust_configuration" }
			},
			{
				"order": 4,
				"name": "Age estimation decision",
				"action": "decision",
				"params": { "rules": [{
					"when": "tf_eval.result == 'accept'",
					"outcome": "accept_estimated",
					"terminate": "accept",
					"reason": "estimated_above_threshold"
				}] }
			},
			{
				"order": 5,
				"name": "Primary document",
				"action": "document_capture",
				"params": {
					"allowedDocuments": [
						{
							"type": "identity-card",
							"countries": [],
							"allCountries": true
						},
						{
							"type": "drivers-licence",
							"countries": [],
							"allCountries": true
						},
						{
							"type": "passport",
							"countries": [],
							"allCountries": true
						}
					],
					"captureBackPage": true
				}
			},
			{
				"order": 6,
				"name": "Age verification",
				"action": "tf_eval",
				"params": { "trustFactorConfig": "default_age_verification_trust_configuration" }
			},
			{
				"order": 7,
				"name": "Age verification decision",
				"action": "decision",
				"params": {
					"rules": [
						{
							"when": "document_capture.result == 'ocr_quality_failed'",
							"outcome": "review_ocr_quality",
							"terminate": "review",
							"reason": "ocr_quality_retry_exhausted"
						},
						{
							"when": "tf_eval.result == 'accept'",
							"outcome": "accept_verified",
							"terminate": "accept",
							"reason": "verified_by_DOB"
						},
						{
							"when": "tf_eval.result == 'reject'",
							"outcome": "reject_tf",
							"terminate": "reject",
							"reason": "trust_reject"
						}
					],
					"default": {
						"outcome": "fallthrough",
						"terminate": "incomplete"
					}
				}
			}
		],
		outcomes: {
			"accept_estimated": { "actions": [] },
			"accept_verified": { "actions": [{ "action": "create_new_customer" }, { "action": "repoint_primary_DI" }] },
			"reject_tf": { "actions": [] },
			"review_ocr_quality": { "actions": [] },
			"fallthrough": { "actions": [] }
		}
	},
	idvOnboardingV1: {
		version: 1,
		workflowName: "idv_onboarding_v1",
		meta: { "label": "Onboarding ID Verification" },
		steps: [
			{
				"order": 1,
				"name": "Mobile redirect",
				"action": "mobile_redirect",
				"params": { "skippable": true }
			},
			{
				"order": 2,
				"name": "Primary document",
				"action": "document_capture",
				"params": {
					"captureBackPage": true,
					"allowedDocuments": [
						{
							"type": "identity-card",
							"countries": [],
							"allCountries": true
						},
						{
							"type": "passport",
							"countries": [],
							"allCountries": true
						},
						{
							"type": "drivers-licence",
							"countries": [],
							"allCountries": true
						}
					]
				}
			},
			{
				"order": 3,
				"name": "Selfie",
				"action": "face_capture"
			},
			{
				"order": 4,
				"name": "Trust factor evaluation",
				"action": "tf_eval",
				"params": { "trustFactorConfig": "default_trust_evaluation_configuration" }
			},
			{
				"order": 5,
				"name": "Face Deduplication",
				"action": "dedup_face",
				"params": { "trustFactorConfig": "default_trust_evaluation_configuration" }
			},
			{
				"order": 6,
				"name": "Document Deduplication",
				"action": "dedup_document"
			},
			{
				"order": 7,
				"name": "Best Duplicate",
				"action": "best_duplicate"
			},
			{
				"order": 8,
				"name": "Decision",
				"action": "decision",
				"params": {
					"rules": [
						{
							"when": "tf_eval.result == 'review'",
							"reason": "trust_review",
							"outcome": "review_tf",
							"terminate": "review"
						},
						{
							"when": "tf_eval.result == 'reject'",
							"reason": "trust_reject",
							"outcome": "reject_tf",
							"terminate": "reject"
						},
						{
							"when": "tf_eval.result == 'accept' && best_duplicate.type == 'blocklist'",
							"outcome": "fraud",
							"terminate": "reject",
							"reason": "blocklist_hit"
						},
						{
							"when": "tf_eval.result == 'accept' && dedup_face.result == 'blocklist'",
							"outcome": "fraud",
							"terminate": "reject",
							"reason": "blocklist_hit"
						},
						{
							"when": "document_capture.result == 'ocr_quality_failed'",
							"outcome": "review_ocr_quality",
							"terminate": "review",
							"reason": "ocr_quality_retry_exhausted"
						},
						{
							"when": "tf_eval.result == 'accept' && best_duplicate.outcome == 'multiple_full_hits'",
							"outcome": "hits",
							"terminate": "accept",
							"reason": "multiple_full_hits"
						},
						{
							"when": "tf_eval.result == 'accept' && best_duplicate.outcome == 'full_hit'",
							"outcome": "hits",
							"terminate": "accept",
							"reason": "full_hit"
						},
						{
							"when": "tf_eval.result == 'accept' && best_duplicate.outcome == 'similar_name'",
							"outcome": "hits",
							"terminate": "accept",
							"reason": "similar_name"
						},
						{
							"when": "tf_eval.result == 'accept' && best_duplicate.outcome == 'face_missmatch'",
							"outcome": "hits",
							"terminate": "accept",
							"reason": "face_missmatch"
						},
						{
							"when": "tf_eval.result == 'accept' && best_duplicate.outcome == 'similar_face'",
							"outcome": "hits",
							"terminate": "accept",
							"reason": "similar_face"
						},
						{
							"when": "tf_eval.result == 'accept' && best_duplicate.outcome == 'doc_missmatch'",
							"outcome": "hits",
							"terminate": "accept",
							"reason": "doc_missmatch"
						},
						{
							"when": "tf_eval.result == 'accept' && dedup_face.result == 'no_match'",
							"outcome": "unique",
							"terminate": "accept"
						},
						{
							"when": "tf_eval.result == 'accept' && dedup_face.result == 'concurrent'",
							"reason": "manual_review_hits",
							"outcome": "hits",
							"terminate": "accept"
						},
						{
							"when": "tf_eval.result == 'accept' && dedup_face.result == 'review'",
							"outcome": "hits",
							"terminate": "accept",
							"reason": "duplicate_pending_review"
						},
						{
							"when": "tf_eval.result == 'accept' && dedup_face.result == 'customer'",
							"outcome": "hits",
							"terminate": "accept"
						}
					],
					"default": {
						"outcome": "fallthrough",
						"terminate": "incomplete"
					}
				}
			}
		],
		outcomes: {
			"merge": { "actions": [{ "action": "add_new_DI_to_customer" }] },
			"unique": { "actions": [{ "action": "create_new_customer" }, { "action": "repoint_primary_DI" }] },
			"hits": { "actions": [{ "action": "1N_move_to_review" }] },
			"reject_tf": { "actions": [] },
			"review_ocr_quality": { "actions": [{ "action": "1N_move_to_review" }] },
			"review_tf": { "actions": [{ "action": "1N_move_to_review" }] },
			"fallthrough": { "actions": [] },
			"fraud": { "actions": [{ "action": "create_blocked_customer" }, { "action": "1N_add_current_to_blocklist" }] }
		}
	},
	biomAuthenticationV1: {
		version: 1,
		workflowName: "biometric_authentication_v1",
		meta: { "label": "Biometric authentication" },
		steps: [
			{
				"order": 1,
				"name": "Mobile redirect",
				"action": "mobile_redirect",
				"params": { "skippable": true }
			},
			{
				"order": 2,
				"name": "Selfie",
				"action": "face_capture"
			},
			{
				"order": 3,
				"name": "Verification",
				"action": "tf_eval",
				"params": { "trustFactorConfig": "default_auth_trust_configuration" }
			},
			{
				"order": 4,
				"name": "Matching",
				"action": "matching",
				"params": { "trustFactorConfig": "default_auth_trust_configuration" }
			},
			{
				"order": 5,
				"name": "Authentication decision",
				"action": "decision",
				"params": {
					"rules": [
						{
							"when": "tf_eval.result == 'accept' && matching.result == 'match'",
							"reason": "verified",
							"outcome": "accept",
							"terminate": "accept"
						},
						{
							"when": "tf_eval.result == 'reject'",
							"reason": "trust_reject",
							"outcome": "reject",
							"terminate": "reject"
						},
						{
							"when": "matching.result == 'no_match'",
							"reason": "matching_reject",
							"outcome": "reject",
							"terminate": "reject"
						}
					],
					"default": {
						"outcome": "fallthrough",
						"terminate": "incomplete"
					}
				}
			}
		],
		outcomes: {
			"accept": { "actions": [{ "action": "add_new_DI_to_customer" }] },
			"reject": { "actions": [] },
			"fallthrough": { "actions": [] }
		}
	},
	docUpdateV1: {
		version: 1,
		workflowName: "doc_update_v1",
		meta: { "label": "Document update" },
		steps: [
			{
				"order": 1,
				"name": "Mobile redirect",
				"action": "mobile_redirect",
				"params": { "skippable": true }
			},
			{
				"order": 2,
				"name": "Primary document",
				"action": "document_capture",
				"params": {
					"captureBackPage": true,
					"allowedDocuments": [
						{
							"type": "identity-card",
							"countries": [],
							"allCountries": true
						},
						{
							"type": "passport",
							"countries": [],
							"allCountries": true
						},
						{
							"type": "drivers-licence",
							"countries": [],
							"allCountries": true
						}
					]
				}
			},
			{
				"order": 3,
				"name": "Selfie",
				"action": "face_capture"
			},
			{
				"order": 4,
				"name": "Trust factor evaluation",
				"action": "tf_eval",
				"params": { "trustFactorConfig": "default_trust_evaluation_configuration" }
			},
			{
				"order": 5,
				"name": "Face Matching",
				"action": "matching",
				"params": { "trustFactorConfig": "default_trust_evaluation_configuration" }
			},
			{
				"order": 6,
				"name": "Decision",
				"action": "decision",
				"params": {
					"rules": [
						{
							"when": "document_capture.result == 'ocr_quality_failed'",
							"outcome": "review_ocr_quality",
							"terminate": "review",
							"reason": "ocr_quality_retry_exhausted"
						},
						{
							"when": "tf_eval.result == 'accept' && matching.result == 'match'",
							"reason": "Verified and updated",
							"outcome": "verified",
							"terminate": "accept"
						},
						{
							"when": "matching.result == 'no_match' && tf_eval.result == 'accept'",
							"reason": "Different person",
							"outcome": "reject_match",
							"terminate": "reject"
						},
						{
							"when": "tf_eval.result == 'review'",
							"reason": "trust_review",
							"outcome": "review_tf",
							"terminate": "review"
						},
						{
							"when": "tf_eval.result == 'reject' && matching.result == 'match'",
							"reason": "trust_reject",
							"outcome": "reject_match",
							"terminate": "reject"
						},
						{
							"when": "tf_eval.result == 'reject' && matching.result == 'no_match'",
							"outcome": "reject_tf",
							"terminate": "accept"
						}
					],
					"default": {
						"outcome": "fallthrough",
						"terminate": "incomplete"
					}
				}
			}
		],
		outcomes: {
			"verified": { "actions": [{ "action": "add_new_DI_to_customer" }, { "action": "repoint_primary_DI" }] },
			"reject_tf": { "actions": [] },
			"review_tf": { "actions": [] },
			"review_ocr_quality": { "actions": [] },
			"fallthrough": { "actions": [] },
			"reject_match": { "actions": [{ "action": "add_new_DI_to_customer" }] }
		}
	}
};
//#endregion
//#region src/workflow/identity-detail-tabs.ts
/** Fixed tab order on Digital Identity Detail (SAIDVU-300; `palms` added by SAIDVU-505). */
const IDENTITY_DETAIL_TAB_IDS = [
	"trust",
	"selfies",
	"palms",
	"documents",
	"matching",
	"duplicates",
	"metadata",
	"auditlog"
];
const FACE_CAPTURE_ACTIONS = /* @__PURE__ */ new Set([
	"face_capture",
	"smile_capture",
	"multirange_capture",
	"smile_liveness",
	"magnifeye_liveness",
	"multirange_liveness"
]);
const DOCUMENT_CAPTURE_ACTIONS = /* @__PURE__ */ new Set([
	"document_capture",
	"nfc_capture",
	"nfc_travel_document_reader"
]);
const MATCHING_ACTIONS = /* @__PURE__ */ new Set(["matching", "palm_matching"]);
const DEDUP_ACTIONS = /* @__PURE__ */ new Set([
	"dedup_face",
	"dedup_facial",
	"dedup_document"
]);
function hasAction(stepActions, candidates) {
	return stepActions.some((action) => candidates.has(action));
}
/** Normalize manifest step `action` values to strings (unknown steps ignored). */
function extractWorkflowStepActions(steps) {
	if (!Array.isArray(steps)) return [];
	const out = [];
	for (const raw of steps) {
		if (!raw || typeof raw !== "object") continue;
		const action = raw.action;
		if (typeof action === "string" && action.trim() !== "") out.push(action);
	}
	return out;
}
/**
* Derive which DI Detail tabs to render from workflow manifest steps (SAIDVU-300).
* When `stepActions` is empty (unknown / historical workflow), optional tabs fall
* back to persisted capture data so older rows stay usable.
*/
function deriveIdentityDetailVisibleTabs(input) {
	const { duplicatesTabEnabled = true, hasDocumentData = false, hasDuplicateData = false, hasMatchEvent = false, hasPalmData = false, hasSelfieData = false, stepActions } = input;
	const knownWorkflow = stepActions.length > 0;
	const faceCapture = hasAction(stepActions, FACE_CAPTURE_ACTIONS);
	const documentCapture = hasAction(stepActions, DOCUMENT_CAPTURE_ACTIONS);
	const matching = hasAction(stepActions, MATCHING_ACTIONS);
	const dedup = hasAction(stepActions, DEDUP_ACTIONS);
	return {
		auditlog: true,
		documents: knownWorkflow ? documentCapture : hasDocumentData,
		duplicates: duplicatesTabEnabled && (knownWorkflow ? dedup : hasDuplicateData),
		matching: knownWorkflow ? matching : hasMatchEvent,
		metadata: true,
		palms: hasPalmData,
		selfies: knownWorkflow ? faceCapture : hasSelfieData,
		trust: true
	};
}
function listVisibleIdentityDetailTabs(visible) {
	return IDENTITY_DETAIL_TAB_IDS.filter((id) => visible[id]);
}
//#endregion
//#region src/dedup-document.ts
/**
* SAIDVU-15 — the spec-14 document-dedup field catalog (AD-7): the ONLY fields a tenant's
* dedup-document config may reference in displayNameFields / comparisons. Single source of
* truth shared by config-service validation (story 1.7) and the dashboard settings screen
* (story 3.1); the engine derives its own snake_case twin from the raw text_data vocabulary.
*/
const DEDUP_DOCUMENT_CATALOG_FIELDS = [
	"fullName",
	"givenNames",
	"surname",
	"middleName",
	"parentNames",
	"surnameAtBirth",
	"secondSurname",
	"mothersSurname",
	"fathersSurname",
	"fathersName",
	"mothersFullName",
	"fathersFullName",
	"mothersBirthSurname",
	"givenNamesNationalLanguage",
	"fullNameNationalLanguage",
	"surnameNationalLanguage",
	"mothersNameNationalLanguage",
	"fathersNameNationalLanguage",
	"dateOfBirth",
	"personalNumber",
	"documentNumber"
];
//#endregion
export { AGE_ESTIMATION_TRUST_CONFIG_NAME, AGE_ESTIMATION_TRUST_CONFIG_OVERLAY, AGE_GATE_TRUST_FACTOR_NAMES, AGE_GATE_TRUST_FACTOR_NAME_SET, AGE_TRUST_CONFIG_OVERLAYS, AGE_VERIFICATION_TRUST_CONFIG_NAME, AGE_VERIFICATION_TRUST_CONFIG_OVERLAY, ANONYMIZED_SENTINEL, AUTH_TRUST_CONFIG_NAME, ActionType, AuditEntityType, BEST_DUPLICATE_OUTCOMES, BEST_DUPLICATE_TYPES, BusinessErrorStatus, ConfigAuditAction, ConnectionType, CustomFlowType, DEDUP_DOCUMENT_CATALOG_FIELDS, DEFAULT_DOCUMENT_OCR_QUALITY_CONFIG, DEFAULT_TRUST_EVALUATION_CONFIG_NAME, DEFAULT_WORKFLOWS, DIS_ERROR_CODE, DOCUMENT_OCR_RETAKE_REASON_CODES, DigitalIdentityAuditAction, DocumentOcrRetakeReasonCode, ERROR_CODES, ERROR_DESCRIPTIONS, ERROR_RESPONSE_ACTION, Environment, ErrorCode, ErrorStatus, FailReason, FilterAll, IDENTITY_DETAIL_TAB_IDS, INNOVATRICS_CONTACT_PAGE, IntegratorAuditAction, LOCALES, Locale, Logger, MOBILE_REDIRECT, MobileRedirectStatus, OnboardingResult, PALM_CAPTURE_REJECTION_REASONS, PALM_HANDS, PALM_TRUST_FACTOR_NAMES, PALM_TRUST_FACTOR_NAME_SET, PII_FIELDS_BY_ENTITY, PlatformHealthStatus, RESET_TO_REVIEW_REASON_MAX_LENGTH, RESET_TO_REVIEW_REASON_MIN_LENGTH, RIVAAS_AUTH_TRANSFER_HASH_KEY, ReviewAuditAction, SANDBOX_TIME_LIMIT_DAYS, SANDBOX_VERIFICATION_LIMIT, SYSTEM_TRUST_CONFIG_NAMES, Step, SystemAuditAction, SystemErrorStatus, TenantAuditAction, URL_VALIDATION_REGEX, UnauthorizedError, VERIFICATION_STATES, VerificationResult, VerificationState, WORKFLOW_ACTIONS_WITH_EXTERNAL_ID_TEMPLATE, WORKFLOW_DECISION_DEFAULT, WORKFLOW_DECISION_RESULT_SIGNALS, WORKFLOW_EXTERNAL_ID_TEMPLATE_VARS, WORKFLOW_FALLTHROUGH_OUTCOME, WORKFLOW_OUTCOME_ACTIONS, WORKFLOW_PALM_STEP_ACTIONS, WORKFLOW_PARAM_LESS_STEP_ACTIONS, WORKFLOW_STEP_ACTIONS, WORKFLOW_STEP_ACTION_LABELS, WORKFLOW_TEMPORAL_STEP_ACTIONS, WORKFLOW_TERMINAL_STATES, WORKFLOW_TRUST_FACTOR_STEP_ACTIONS, WORKFLOW_UI_STEP_ACTIONS, WebHookEventType, decodeRivaasAuthTransfer, decodeRivaasAuthTransferPayload, deriveActorType, deriveIdentityDetailVisibleTabs, encodeRivaasAuthTransfer, encodeRivaasAuthTransferPayload, extractWorkflowStepActions, generateUID, getOrganizationIdFromHeaders, initPinoLogger, isValidResetToReviewReasonText, listVisibleIdentityDetailTabs, migrateDedupVocabulary, normalizeResetToReviewReasonText, normalizeWorkflow, otherPalmHand, parseRivaasAuthTransferFromHash, removeTrailingSlash, sanitizeBody, sanitizeHeaders, validateWorkflow, workflowContainsPalmSteps, workflowRequiresCustomerId };

//# sourceMappingURL=index.mjs.map